import { useEffect, useState, type FormEvent } from 'react'
import { KeyRound, Plus, Server, Shield } from 'lucide-react'
import { api } from '../../api/client'
import type { APIKey, Settings } from '../../api/types'
import { PageHeader } from '../../components/PageHeader'
import { Modal } from '../../components/Modal'
import { useI18n } from '../../i18n'

export function AdminSettingsPage() {
  const { t } = useI18n()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [keys, setKeys] = useState<APIKey[]>([])
  const [status, setStatus] = useState('')
  const [secret, setSecret] = useState('')
  const [keyModal, setKeyModal] = useState(false)
  const [keyName, setKeyName] = useState('')

  useEffect(() => {
    api.get<Settings>('/admin/settings').then(setSettings).catch((e: Error) => setStatus(e.message))
    api.get<APIKey[]>('/admin/api-keys').then(setKeys).catch(() => {})
  }, [])

  async function saveGeneral(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      const next: Settings = {
        attachmentLimitMb: Number(form.get('attachmentLimitMb')),
        forwardLimit: Number(form.get('forwardLimit')),
        publicRegistration: form.get('publicRegistration') === 'on',
        invitationRequired: form.get('invitationRequired') === 'on',
        spamThreshold: settings?.spamThreshold ?? 6,
        logoUrl: String(form.get('logoUrl') ?? '').trim(),
        serverIp: String(form.get('serverIp') ?? '').trim(),
        mailDomain: String(form.get('mailDomain') ?? '').trim(),
        smtpHost: String(form.get('smtpHost') ?? '').trim(),
        smtpPort: String(form.get('smtpPort') ?? '').trim(),
      }
      await api.patch('/admin/settings', next)
      setSettings(next)
      setStatus(t('admin.saveSettings') + ' ✓')
      setTimeout(() => setStatus(''), 3000)
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Error') }
  }

  async function createKey() {
    if (!keyName.trim()) return
    try {
      const result = await api.post<{ apiKey: APIKey; secret: string }>('/admin/api-keys', { name: keyName.trim() })
      setKeys((current) => [result.apiKey, ...current])
      setSecret(result.secret)
      setKeyModal(false)
      setKeyName('')
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Error') }
  }

  return <>
    <PageHeader title="admin.settings" description="admin.settingsDescription" />
    {status && <p className="mb-5 rounded-lg bg-primary/10 px-4 py-2.5 text-sm text-primary">{status}</p>}

    <div className="grid gap-6 xl:grid-cols-2">
      <form key={settings ? 'loaded' : 'loading'} onSubmit={saveGeneral} className="panel p-5 sm:p-6 space-y-6">
        <section>
          <h2 className="flex items-center gap-2 text-base font-semibold"><Shield size={18} className="text-primary" />{t('admin.general')}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">{t('admin.attachmentLimit')}<input className="field mt-1.5" name="attachmentLimitMb" type="number" min="1" defaultValue={settings?.attachmentLimitMb ?? 25} /></label>
            <label className="text-sm font-medium">{t('admin.forwardLimit')}<input className="field mt-1.5" name="forwardLimit" type="number" min="1" defaultValue={settings?.forwardLimit ?? 50} /></label>
          </div>
          <div className="mt-4">
            <label className="text-sm font-medium">{t('admin.logoUrl')}<input className="field mt-1.5" name="logoUrl" type="url" placeholder="https://example.com/logo.png" defaultValue={settings?.logoUrl ?? ''} /></label>
          </div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm"><input name="publicRegistration" type="checkbox" className="rounded" defaultChecked={settings?.publicRegistration} />{t('admin.publicRegistration')}</label>
            <label className="flex items-center gap-2 text-sm"><input name="invitationRequired" type="checkbox" className="rounded" defaultChecked={settings?.invitationRequired} />{t('admin.invitationRequired')}</label>
          </div>
        </section>

        <section className="border-t pt-6">
          <h2 className="flex items-center gap-2 text-base font-semibold"><Server size={18} className="text-primary" />{t('admin.serverConfig')}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t('admin.serverConfigDesc')}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">{t('admin.mailDomain')}<input className="field mt-1.5" name="mailDomain" placeholder="mail.example.com" defaultValue={settings?.mailDomain ?? ''} /></label>
            <label className="text-sm font-medium">{t('admin.serverIp')}<input className="field mt-1.5" name="serverIp" placeholder="198.23.212.26" defaultValue={settings?.serverIp ?? ''} /></label>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">{t('admin.smtpHost')}<input className="field mt-1.5" name="smtpHost" placeholder="postfix" defaultValue={settings?.smtpHost ?? ''} /></label>
            <label className="text-sm font-medium">{t('admin.smtpPort')}<input className="field mt-1.5" name="smtpPort" placeholder="587" defaultValue={settings?.smtpPort ?? ''} /></label>
          </div>
        </section>

        <button className="btn-primary">{t('admin.saveSettings')}</button>
      </form>

      <section className="panel p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold"><KeyRound size={18} className="text-primary" />{t('admin.apiKeys')}</h2>
            <p className="text-sm text-muted-foreground">{t('admin.apiKeysDescription')}</p>
          </div>
          <button type="button" onClick={() => setKeyModal(true)} className="btn-primary flex items-center gap-2 shrink-0"><Plus size={15} />{t('admin.createKey')}</button>
        </div>
        {secret && <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-950"><KeyRound className="mr-2 inline" size={16} /><code className="break-all">{secret}</code></div>}
        <div className="mt-4 max-h-48 divide-y overflow-y-auto scrollbar-thin">
          {keys.map((key) => <div className="flex justify-between py-3 text-sm" key={key.id}><span>{key.name}</span><code className="text-muted-foreground">{key.prefix}…</code></div>)}
        </div>
      </section>
    </div>

    <Modal open={keyModal} onClose={() => { setKeyModal(false); setKeyName('') }} title={t('admin.createKey')}>
      <div className="space-y-4">
        <input className="field" placeholder="API key name" value={keyName} onChange={(e) => setKeyName(e.target.value)} autoFocus />
        <div className="flex gap-3">
          <button onClick={createKey} disabled={!keyName.trim()} className="btn-primary disabled:opacity-50">{t('admin.createKey')}</button>
          <button onClick={() => { setKeyModal(false); setKeyName('') }} className="btn-secondary">{t('common.cancel')}</button>
        </div>
      </div>
    </Modal>
  </>
}
